import React from "react";
import RuleRow, { RuleProps } from "./RuleRow";
import clsx from "clsx";
import RulesHeader from "../components/RulesHeader";
import RulesFooter from "../components/RulesFooter";
import { useTranslation } from "react-i18next";
import { rulesSetStore } from "../RulesSetStore";
import { observer } from "mobx-react-lite";
import { EMPTY_CONDITION } from "./RuleConditions";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import useReorderList from "hooks/useReorderList";

interface RulesListProps {
  showOverview: boolean;
  setShowOverview: (state: boolean) => void;
}

const RulesList = (props: RulesListProps) => {
  const { t } = useTranslation();
  const [isHeaderSticky, setIsHeaderSticky] = React.useState<boolean>(false);

  const openOverview = (rule: RuleProps) => {
    rulesSetStore.setCurrentRule(rule);
    rulesSetStore.setInProgressRuleID(undefined);
    props.setShowOverview(true);
  };

  const addNewRule = async () => {
    const newRule = {
      id: rulesSetStore.rulesList.length + 1,
      name: `${t("rulesEditor.newRule")} ${rulesSetStore.rulesList.length + 1}`,
      conditions: [{ ...EMPTY_CONDITION }],
    };
    await rulesSetStore.createNewRule(newRule);
  };

  const onScrollList = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const containerElement = e.target as HTMLDivElement;
    setIsHeaderSticky(containerElement.scrollTop !== 0);
  };

  async function initialCheck() {
    if (rulesSetStore.rulesList.length === 0) {
      await addNewRule();
    }
    rulesSetStore.setCurrentRule(rulesSetStore.rulesList[0]);
  }

  React.useEffect(() => {
    initialCheck();
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = rulesSetStore.rulesList.findIndex(
        (item) => item.id === active.id
      );
      const newIndex = rulesSetStore.rulesList.findIndex(
        (item) => item.id === over?.id
      );

      const reorderedItems = arrayMove(
        rulesSetStore.rulesList,
        oldIndex,
        newIndex
      );

      rulesSetStore.setRulesList(reorderedItems);
      await rulesSetStore.updateRulesOrders();
    }
  };

  const {
    items: reorderedList,
    moveUp,
    moveDown,
    moveToTop,
    moveToBottom,
  } = useReorderList(rulesSetStore.rulesList);

  React.useEffect(() => {
    rulesSetStore.setRulesList(reorderedList);
    rulesSetStore.updateRulesOrders();
  }, [reorderedList]);

  return (
    <div
      className={clsx("col-span-1 flex flex-col h-[calc(100vh-65px)]", {
        "col-span-2": !props.showOverview,
        "border-r border-gray-300": props.showOverview,
      })}
    >
      <RulesHeader onAdd={addNewRule} showShadow={isHeaderSticky} />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={rulesSetStore.rulesList.map((row) => row.id)}>
          <ul className="flex-1 overflow-y-auto" onScroll={onScrollList}>
            {rulesSetStore.rulesList.map((item, index) => (
              <RuleRow
                key={item.id}
                index={index}
                rule={item}
                onClick={() => openOverview(item)}
                moveUp={moveUp}
                moveDown={moveDown}
                moveToTop={moveToTop}
                moveToBottom={moveToBottom}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      <RulesFooter dataCount={rulesSetStore.rulesList.length} />
    </div>
  );
};

export default observer(RulesList);
